"use client";

import { format } from "date-fns";
import { useState } from "react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "../ui";
import { DateRangePickerCalendar } from "./date-range-picker-calendar";
import { DatePresetHours } from "./date-preset-picker";
import type { DatePreset } from "~/types/ui";
import { isRelativeTime } from "~/utils/date-util";

/** Picks only a compare range's starting point — its length always follows the main
 * chart's date range (see `resolveCompareRange`).
 *
 * A preset sets a relative offset measured back from the *main range's start*,
 * so "7d" means "the week before whatever is currently shown", and keeps meaning
 * that when the main range moves. The calendar sets an absolute starting date/time
 * instead, pinning the comparison to one specific window. */
export function CompareStartPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (from: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const selectedPreset = (Object.keys(DatePresetHours) as DatePreset[]).find(
    (preset) => `-${preset}` === value,
  );

  const presets = (Object.keys(DatePresetHours) as DatePreset[]).map(
    (preset, i) => (
      <Button
        type="button"
        variant={selectedPreset === preset ? "default" : "outline"}
        key={preset}
        className={`px-2 py-1 ${i === 0 ? "rounded-none rounded-l-md" : "rounded-none"} text-sm`}
        onClick={() => onChange(`-${preset}`)}
      >
        {preset}
      </Button>
    ),
  );

  return (
    <div className="flex">
      {presets}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={selectedPreset === undefined ? "default" : "outline"}
            className="py-1 rounded-none rounded-r-md text-sm"
            onClick={() => setOpen(true)}
          >
            {isRelativeTime(value)
              ? value
              : format(new Date(value), "yyyy/MM/dd HH:mm")}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          onWheel={(e) => e.stopPropagation()}
        >
          {/* Stays open on a pick, like the main range picker: the calendar reports a
              day click and every hour/minute click separately, so closing on the first
              would make it impossible to set a day and a time together. */}
          <DateRangePickerCalendar
            date={isRelativeTime(value) ? undefined : new Date(value)}
            setDate={(date) => onChange(date.toISOString())}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
